import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from '@/components/ui/command';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import {
  LayoutDashboard, Bell, Coins, Gem, Circle, Hammer, FileText, Boxes, Store,
  Users, UserCog, BarChart3, ScrollText, Loader2, FileDigit, ChevronRight, Search, CalendarClock,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';

/**
 * CommandPalette — global Ctrl/Cmd+K quick launcher.
 *
 * Jump to any page, or type to jump straight to an Order, Customer, Goldsmith
 * or Page No. Static "Pages" are filtered client-side; entities are fetched
 * from the lightweight `/search/quick` endpoint (debounced, company-scoped).
 *
 * Server-side results mean we disable cmdk's built-in fuzzy filter
 * (`shouldFilter={false}`) and render exactly what the backend returns.
 */

const PAGES = [
  { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard, keywords: 'home' },
  { label: 'Order Forms', path: '/orders', icon: FileText, keywords: 'orders jobs' },
  { label: 'Customers', path: '/customers', icon: Users, keywords: 'buyers clients' },
  { label: 'Goldsmiths', path: '/goldsmiths', icon: Hammer, keywords: 'karigar' },
  { label: 'Gold', path: '/gold', icon: Coins, keywords: 'loose gold ledger' },
  { label: 'Diamonds', path: '/diamonds', icon: Gem, keywords: 'stones' },
  { label: 'Colour Stones', path: '/color-stones', icon: Circle, keywords: 'color stones' },
  { label: 'Pearls', path: '/pearls', icon: Circle, keywords: 'pearl' },
  { label: 'Mountings', path: '/mountings', icon: Boxes, keywords: 'semi finished' },
  { label: 'Jewellery', path: '/jewellery', icon: Gem, keywords: 'finished pieces' },
  { label: 'Vendors', path: '/vendors', icon: Store, keywords: 'suppliers' },
  { label: 'Cutters', path: '/cutters', icon: Hammer, keywords: 'cutter' },
  { label: 'Reports', path: '/reports', icon: BarChart3, keywords: 'report closing stock' },
  { label: 'Activity Digest', path: '/reports/digest', icon: CalendarClock, keywords: 'digest activity report daily summary' },
  { label: 'Memos', path: '/reports/memos', icon: ScrollText, keywords: 'issue return memos' },
  { label: 'Production Timeline', path: '/production-timeline', icon: BarChart3, keywords: 'sla delays' },
  { label: 'Notifications', path: '/notifications', icon: Bell, keywords: 'alerts bell' },
  { label: 'Companies (Admin)', path: '/admin/companies', icon: UserCog, keywords: 'admin company export restore' },
  { label: 'Users (Admin)', path: '/admin/users', icon: UserCog, keywords: 'admin accounts' },
];

const EMPTY = { orders: [], customers: [], goldsmiths: [], page_nos: [], diamond_packets: [] };

export default function CommandPalette() {
  const { effectiveCompanyId } = useAuth();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const seqRef = useRef(0);
  const debounceRef = useRef(null);

  // Global Ctrl/Cmd+K toggles the palette from anywhere.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Reset transient state each time the palette opens.
  useEffect(() => {
    if (open) { setQuery(''); setResults(EMPTY); setLoading(false); }
  }, [open]);

  // Debounced server search for entities (pages are filtered locally).
  useEffect(() => {
    if (!open) return undefined;
    const term = query.trim();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (term.length < 1 || !effectiveCompanyId) {
      setResults(EMPTY); setLoading(false);
      return undefined;
    }
    setLoading(true);
    const seq = ++seqRef.current;
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get('/search/quick', {
          params: { q: term, company_id: effectiveCompanyId },
        });
        if (seq === seqRef.current) setResults(data || EMPTY);
      } catch {
        if (seq === seqRef.current) setResults(EMPTY);
      } finally {
        if (seq === seqRef.current) setLoading(false);
      }
    }, 200);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, open, effectiveCompanyId]);

  // Everything launched from the palette opens in a NEW TAB so the user's
  // current tab (and whatever they were doing) stays undisturbed.
  const go = useCallback((path) => {
    setOpen(false);
    window.open(path, '_blank', 'noopener,noreferrer');
  }, []);

  const filteredPages = useMemo(() => {
    const t = query.trim().toLowerCase();
    if (!t) return PAGES;
    return PAGES.filter((p) => (p.label + ' ' + (p.keywords || '')).toLowerCase().includes(t));
  }, [query]);

  const hasEntity = results.orders.length || results.customers.length
    || results.goldsmiths.length || results.page_nos.length || results.diamond_packets.length;
  // The "Filter Order Forms by …" action always renders while the user is
  // typing, so the palette is never truly empty in that case.
  const nothing = !loading && filteredPages.length === 0 && !hasEntity && !query.trim();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="overflow-hidden p-0 max-w-xl gap-0" data-testid="command-palette">
        <DialogTitle className="sr-only">Quick Launcher</DialogTitle>
        <Command shouldFilter={false} className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-slate-400">
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder="Jump to a page, order, customer, goldsmith, page no or diamond packet…"
            data-testid="command-palette-input"
          />
          <CommandList className="max-h-[420px]">
            {nothing && <CommandEmpty data-testid="command-palette-empty">No matches found.</CommandEmpty>}

            {/* Type an order no / page no / reference — jump straight to the
                Order Forms page with the list filtered by what was typed. */}
            {query.trim() && (
              <CommandGroup heading="Order Forms">
                <CommandItem
                  value={`orderforms-search:${query.trim()}`}
                  onSelect={() => go(`/orders?search=${encodeURIComponent(query.trim())}`)}
                  className="cursor-pointer"
                  data-testid="command-order-forms-search"
                >
                  <Search className="text-indigo-500" />
                  <span>Filter Order Forms by <span className="font-mono text-[12px]">&ldquo;{query.trim()}&rdquo;</span></span>
                  <span className="ml-auto text-[11px] text-slate-400">order · page no · customer</span>
                </CommandItem>
              </CommandGroup>
            )}

            {filteredPages.length > 0 && (
              <CommandGroup heading="Pages">
                {filteredPages.map((p) => {
                  const Icon = p.icon;
                  return (
                    <CommandItem
                      key={p.path}
                      value={`page:${p.label}`}
                      onSelect={() => go(p.path)}
                      className="cursor-pointer"
                      data-testid={`command-page-${p.path}`}
                    >
                      <Icon className="text-slate-500" />
                      <span>{p.label}</span>
                      <ChevronRight className="ml-auto text-slate-300" size={14} />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}

            {loading && (
              <div className="flex items-center gap-2 px-3 py-3 text-sm text-slate-400" data-testid="command-palette-loading">
                <Loader2 size={14} className="animate-spin" /> Searching…
              </div>
            )}

            {results.orders.length > 0 && (
              <CommandGroup heading="Orders">
                {results.orders.map((o) => (
                  <CommandItem
                    key={o.id}
                    value={`order:${o.id}`}
                    onSelect={() => go(`/orders/${o.id}`)}
                    className="cursor-pointer"
                    data-testid={`command-order-${o.id}`}
                  >
                    <FileText className="text-indigo-500" />
                    <span className="font-mono text-[12px]">{o.order_no}</span>
                    <span className="truncate text-slate-500 text-[12px]">
                      {o.title || o.customer_name || ''}
                    </span>
                    {o.reference_no != null && o.reference_no !== '' && (
                      <span className="ml-auto text-[11px] text-indigo-600 bg-indigo-50 border border-indigo-200 rounded px-1.5 py-0.5">
                        #{o.reference_no}
                      </span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {results.customers.length > 0 && (
              <CommandGroup heading="Customers">
                {results.customers.map((c) => (
                  <CommandItem
                    key={c.id}
                    value={`customer:${c.id}`}
                    onSelect={() => go(`/customers?q=${encodeURIComponent(c.name || '')}`)}
                    className="cursor-pointer"
                    data-testid={`command-customer-${c.id}`}
                  >
                    <Users className="text-emerald-500" />
                    <span>{c.name}</span>
                    {c.phone && <span className="ml-auto text-[12px] text-slate-400">{c.phone}</span>}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {results.goldsmiths.length > 0 && (
              <CommandGroup heading="Goldsmiths">
                {results.goldsmiths.map((g) => (
                  <CommandItem
                    key={g.id}
                    value={`goldsmith:${g.id}`}
                    onSelect={() => go(`/goldsmiths/${g.id}`)}
                    className="cursor-pointer"
                    data-testid={`command-goldsmith-${g.id}`}
                  >
                    <Hammer className="text-amber-500" />
                    <span>{g.name}</span>
                    {g.phone && <span className="ml-auto text-[12px] text-slate-400">{g.phone}</span>}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {results.page_nos.length > 0 && (
              <CommandGroup heading="Page Nos">
                {results.page_nos.map((no) => (
                  <CommandItem
                    key={`ref-${no}`}
                    value={`pageno:${no}`}
                    onSelect={() => go(`/orders?reference_no=${encodeURIComponent(no)}`)}
                    className="cursor-pointer"
                    data-testid={`command-pageno-${no}`}
                  >
                    <FileDigit className="text-violet-500" />
                    <span>Page No <span className="font-mono">{no}</span></span>
                    <span className="ml-auto text-[11px] text-slate-400">view orders</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {results.diamond_packets.length > 0 && (
              <CommandGroup heading="Diamond Packets">
                {results.diamond_packets.map((p) => (
                  <CommandItem
                    key={`pkt-${p.id}`}
                    value={`packet:${p.id}`}
                    onSelect={() => go(`/diamonds?search=${encodeURIComponent(p.packet_no || '')}`)}
                    className="cursor-pointer"
                    data-testid={`command-packet-${p.id}`}
                  >
                    <Gem className="text-sky-500" />
                    <span className="font-mono text-[12px]">{p.packet_no}</span>
                    <span className="ml-auto text-[11px] text-slate-400 font-mono tabular-nums">
                      {(Number(p.balance_carats || 0)).toFixed(3)} ct · {Number(p.balance_pieces || 0)} pc
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
